import React, { useState, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import { newtonProtocolsAPI } from '../../api';

const FileUploadModal = ({ onSuccess, onCancel }) => {
  const { mode } = useTheme();
  
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  
  // Validar tipo de archivo
  const isValidFile = (file) => {
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return validExtensions.includes(fileExt);
  };
  
  // Manejar selección de archivo
  const handleFileSelect = useCallback((selectedFile) => {
    setError(null);
    
    if (!isValidFile(selectedFile)) {
      setError('Formato de archivo no válido. Solo se aceptan CSV y Excel (.xlsx, .xls)');
      return;
    }
    
    // Validar tamaño (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError('El archivo es demasiado grande. Máximo: 50MB');
      return;
    }
    
    setFile(selectedFile);
    setFileName(selectedFile.name);
    
    // Auto-generar nombre de fuente si está vacío
    if (!sourceName) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
      setSourceName(nameWithoutExt);
    }
  }, [sourceName]);
  
  // Drag & Drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [handleFileSelect]);
  
  // Input file change
  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };
  
  // Subir archivo
  const handleUpload = async () => {
    if (!file || !sourceName.trim()) {
      setError('Debes seleccionar un archivo y proporcionar un nombre');
      return;
    }
    
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', sourceName.trim());
      
      // Simular progreso
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);
      
      const response = await newtonProtocolsAPI.dataSources.uploadFile(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (response.success) {
        setPreview(response.data);
        
        // Llamar onSuccess después de un breve delay para mostrar preview
        setTimeout(() => {
          onSuccess(response.data);
        }, 1500);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.detail || 'Error subiendo archivo');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };
  
  // Estilos
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 'var(--spacing-lg)'
    },
    modal: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-xl)',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    header: {
      padding: 'var(--spacing-xl)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      margin: 0
    },
    closeButton: {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: 'var(--font-size-2xl)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      cursor: 'pointer',
      padding: 'var(--spacing-xs)',
      lineHeight: 1,
      transition: 'color 0.2s ease'
    },
    body: {
      padding: 'var(--spacing-xl)'
    },
    dropZone: {
      border: `2px dashed ${dragging ? '#29B5E8' : (mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-medium-gray)')}`,
      borderRadius: 'var(--border-radius-lg)',
      padding: 'var(--spacing-3xl)',
      textAlign: 'center',
      backgroundColor: dragging 
        ? (mode === 'dark' ? 'rgba(41, 181, 232, 0.1)' : 'rgba(41, 181, 232, 0.05)')
        : (mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)'),
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginBottom: 'var(--spacing-lg)'
    },
    dropZoneIcon: {
      fontSize: '48px',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.6
    },
    dropZoneText: {
      fontSize: 'var(--font-size-lg)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 'var(--spacing-sm)',
      fontWeight: 'var(--font-weight-semibold)'
    },
    dropZoneSubtext: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    fileInput: {
      display: 'none'
    },
    formGroup: {
      marginBottom: 'var(--spacing-lg)'
    },
    label: {
      display: 'block',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-dark-gray)',
      marginBottom: 'var(--spacing-xs)'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-sm) var(--spacing-md)',
      fontSize: 'var(--font-size-base)',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-input-border)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-input-bg)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-input-text)' : 'var(--color-black)',
      boxSizing: 'border-box'
    },
    selectedFile: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-md)',
      padding: 'var(--spacing-md)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      borderRadius: 'var(--border-radius-md)',
      marginBottom: 'var(--spacing-lg)'
    },
    fileIcon: {
      fontSize: 'var(--font-size-3xl)'
    },
    fileInfo: {
      flex: 1,
      minWidth: 0
    },
    fileNameText: {
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 'var(--spacing-xs)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    fileSize: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    removeButton: {
      padding: 'var(--spacing-xs) var(--spacing-sm)',
      backgroundColor: 'rgba(255, 107, 53, 0.1)',
      border: '1px solid #FF6B35',
      borderRadius: 'var(--border-radius-md)',
      color: '#FF6B35',
      fontSize: 'var(--font-size-sm)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    progressContainer: {
      marginBottom: 'var(--spacing-lg)'
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: 'var(--spacing-sm)'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#29B5E8',
      transition: 'width 0.3s ease',
      width: `${uploadProgress}%`
    },
    progressText: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      textAlign: 'center'
    },
    previewContainer: {
      marginTop: 'var(--spacing-lg)',
      padding: 'var(--spacing-lg)',
      backgroundColor: mode === 'dark' ? 'rgba(56, 193, 114, 0.1)' : 'rgba(56, 193, 114, 0.05)',
      borderRadius: 'var(--border-radius-lg)',
      border: '1px solid #38C172'
    },
    previewTitle: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      color: '#38C172',
      marginBottom: 'var(--spacing-md)'
    },
    previewStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 'var(--spacing-md)',
      marginBottom: 'var(--spacing-md)'
    },
    previewStat: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    previewStatValue: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginRight: 'var(--spacing-xs)'
    },
    footer: {
      padding: 'var(--spacing-xl)',
      borderTop: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      display: 'flex',
      gap: 'var(--spacing-md)',
      justifyContent: 'flex-end'
    },
    errorMessage: {
      padding: 'var(--spacing-md)',
      backgroundColor: 'rgba(255, 107, 53, 0.1)',
      border: '1px solid #FF6B35',
      borderRadius: 'var(--border-radius-md)',
      color: '#FF6B35',
      fontSize: 'var(--font-size-sm)',
      marginBottom: 'var(--spacing-lg)'
    }
  };
  
  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>📁 Subir Archivo</h2>
          <button 
            style={styles.closeButton}
            onClick={onCancel}
            disabled={uploading}
          >
            ×
          </button>
        </div>
        
        {/* Body */}
        <div style={styles.body}>
          {error && (
            <div style={styles.errorMessage}>
              ⚠ {error}
            </div>
          )}
          
          {/* Drop Zone */}
          {!file && (
            <div
              style={styles.dropZone}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <div style={styles.dropZoneIcon}>
                {dragging ? '📂' : '📄'}
              </div>
              <div style={styles.dropZoneText}>
                {dragging ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para seleccionar'}
              </div>
              <div style={styles.dropZoneSubtext}>
                Formatos: CSV, Excel (.xlsx, .xls) - Máximo: 50MB
              </div>
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileInputChange}
                style={styles.fileInput}
              />
            </div>
          )}
          
          {/* Selected File */}
          {file && !preview && (
            <div style={styles.selectedFile}>
              <div style={styles.fileIcon}>
                {file.name.endsWith('.csv') ? '📊' : '📗'}
              </div>
              <div style={styles.fileInfo}>
                <div style={styles.fileNameText}>{fileName}</div>
                <div style={styles.fileSize}>{formatFileSize(file.size)}</div>
              </div>
              {!uploading && (
                <button
                  style={styles.removeButton}
                  onClick={() => {
                    setFile(null);
                    setFileName('');
                    setError(null);
                  }}
                >
                  🗑️ Quitar
                </button>
              )}
            </div>
          )}
          
          {/* Upload Progress */}
          {uploading && (
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={styles.progressFill} />
              </div>
              <div style={styles.progressText}>
                Subiendo archivo... {uploadProgress}%
              </div>
            </div>
          )}
          
          {/* Preview */}
          {preview && (
            <div style={styles.previewContainer}>
              <div style={styles.previewTitle}>
                ✅ Archivo procesado exitosamente
              </div>
              <div style={styles.previewStats}>
                <div style={styles.previewStat}>
                  <span style={styles.previewStatValue}>{preview.row_count.toLocaleString()}</span>
                  filas
                </div>
                <div style={styles.previewStat}>
                  <span style={styles.previewStatValue}>{preview.column_count}</span>
                  columnas
                </div>
                <div style={styles.previewStat}>
                  <span style={styles.previewStatValue}>{formatFileSize(preview.file_size)}</span>
                  tamaño
                </div>
                <div style={styles.previewStat}>
                  <span style={styles.previewStatValue}>{preview.encoding}</span>
                  encoding
                </div>
              </div>
            </div>
          )}
          
          {/* Source Name Input */}
          {file && !uploading && !preview && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Nombre de la Fuente *</label>
              <input
                type="text"
                style={styles.input}
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                placeholder="Ej: Inventario Octubre 2025"
                disabled={uploading}
              />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={styles.footer}>
          <button 
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={uploading}
          >
            Cancelar
          </button>
          {file && !preview && (
            <button 
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !sourceName.trim()}
            >
              {uploading ? '⏳ Subiendo...' : '📤 Subir Archivo'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;

